/**
 * Email Folder Interface
 * Used for TreeView navigation in email sidebar
 *
 * Matches Outlook datasource structure exactly (uppercase properties)
 * Reference: folderData from outlook-master/src/app/data/datasource.ts
 *
 * TODO: Align with backend API response when available
 * API Endpoint: GET /api/v1/email-folders
 */

import { EmailFolder } from './email-message.interface';

/**
 * Outlook-compatible folder structure (matches reference datasource exactly)
 */
export interface IEmailFolder {
  // Outlook exact property names (uppercase)
  ID: number;
  PID: number | null;
  Name: string;
  HasChild: boolean;
  Expanded: boolean;
  Count: number | string;
  Favorite?: 'Favorite' | 'Favorite-Composite';
  FavoriteMessage?: string;

  // Additional ERP properties (lowercase for backward compatibility)
  id?: number;
  parentId?: number | null;
  name?: string;
  hasChild?: boolean;
  expanded?: boolean;
  count?: number | string;
  favorite?: 'Favorite' | 'Favorite-Composite';
  favoriteMessage?: string;
  icon?: string;
  folderType?: EmailFolder | string;

  // NEW: API properties from email_folders table (dynamic folder sync)
  // These are populated when folders are fetched from the backend API
  dbId?: number;                    // Local database ID (email_folders.id)
  providerId?: string;              // Provider folder ID (e.g., Outlook's AAMkAGI...)
  providerFolderId?: string;        // Alias for providerId (used by Move To API)
  isSystemFolder?: boolean;         // True for Inbox, Sent, Drafts, Trash, Spam, Archive
  isFavorite?: boolean;             // User-marked favorite
  unreadCount?: number;             // Unread email count in this folder
  totalCount?: number;              // Total email count in this folder
  childFolderCount?: number;        // Number of child folders
  sortOrder?: number;               // Display order (system folders first)
  lastSyncedAt?: string;            // ISO8601 timestamp of last sync

  // Service properties
  accountId?: string;               // Account ID for multi-account support
  createdAt?: Date;                 // Creation timestamp
  updatedAt?: Date;                 // Last update timestamp
  isLoading?: boolean;              // Loading state for UI
  subFoldersLoaded?: boolean;       // Flag indicating if subfolders have been loaded
}

/**
 * TreeView field mapping for Syncfusion ejs-treeview
 * Matches Outlook reference exactly: ID, Name, PID, HasChild, Expanded
 */
export const EMAIL_FOLDER_FIELDS = {
  dataSource: [],
  id: 'ID',           // Outlook exact
  text: 'Name',       // Outlook exact
  parentID: 'PID',    // Outlook exact
  hasChildren: 'HasChild',  // Outlook exact
  expanded: 'Expanded'      // Outlook exact
};

/**
 * Default folder icons (Material Icons)
 */
export const FOLDER_ICONS: { [key: string]: string } = {
  'Favorites': 'star',
  'Inbox': 'inbox',
  'Sent Items': 'send',
  'Drafts': 'drafts',
  'Deleted Items': 'delete',
  'Archive': 'archive',
  'Junk Mail': 'report',
  'Outbox': 'outbox',
  'Clutter': 'folder',
  'Trash': 'delete_forever',
  'Sales Reports': 'assessment',
  'Marketing Reports': 'trending_up',
  'Personnel': 'people',
  'My Team': 'group'
};

/**
 * System folder names that cannot be renamed or deleted
 */
export const SYSTEM_FOLDER_NAMES = [
  'Inbox',
  'Sent Items',
  'Drafts',
  'Deleted Items',
  'Archive',
  'Junk Mail',
  'Outbox',
  'Trash',
  'Spam'
];

/**
 * Result interface for folder operations
 */
export interface IFolderOperationResult {
  success: boolean;
  message?: string;
  error?: string;
  folder?: IEmailFolder;
}

/**
 * Result interface for folder move operations
 */
export interface IFolderMoveResult {
  success: boolean;
  message?: string;
  error?: string;
  movedFolder?: IEmailFolder;
  oldParentId?: number | null;
  newParentId?: number | null;
}

/**
 * Result interface for subfolder lazy loading
 */
export interface ISubfolderLoadResult {
  folders: IEmailFolder[];
  hasMore: boolean;
  totalCount: number;
  offset?: number;
}
